/* eslint-disable import/no-cycle */
import { Dispatch } from 'react';
import { Session } from 'sip.js';
import {
  log,
  conferenceMixer,
  getLocalAudioStats,
  reportMicrophoneHealth,
  evaluateMicSignal,
  MIC_CHECK_INITIAL_DELAY_MS,
  MIC_CHECK_INTERVAL_MS,
  MIC_DEAD_CONSECUTIVE_SAMPLES,
  MIC_DEAD_CONSECUTIVE_SAMPLES_AFTER_AUDIO,
} from '~/helpers';
import { LocalAudioStats } from '~/helpers/reportMediaError';
import { RootState } from '~/store/config';
import {
  showMicAudioWarning,
  dismissMicAudioWarning,
} from '~/components/MicAudioWarning';
import { setVoiceSettingsOpen } from './system';
import * as actionTypes from './actionTypes';

export interface MicSignalMonitor {
  stop: () => void;
}

interface MicSignalMonitorArgs {
  session: Session;
  /** True for the outgoing leg, i.e. this side sent the INVITE. */
  isInvite: boolean;
  dispatch: Dispatch<any>;
  getState: () => RootState;
}

/**
 * The window of the very first check runs from call establish, where both cumulative counters
 * were zero.
 */
const ESTABLISH_BASELINE: LocalAudioStats = {
  totalAudioEnergy: 0,
  totalSamplesDuration: 0,
};

/**
 * @name startMicSignalMonitor
 * @description Watch the local microphone for the whole call and warn the user when it stops
 * producing audio, so a one-way call is caught while it can still be fixed instead of turning
 * into a support ticket the next day.
 *
 * The first verdict is taken `MIC_CHECK_INITIAL_DELAY_MS` after establish and covers the whole
 * period since, then the microphone is re-checked every `MIC_CHECK_INTERVAL_MS`. Polling
 * continues even while no warning is up, because a microphone can also die at minute three.
 *
 * Nearly all of the logic here is about NOT crying wolf. A call where nobody has spoken yet is
 * the normal case, and in this app both mute and hold are expressed as `track.enabled = false`,
 * which produces exactly the digital silence a dead device produces. So every state that can
 * legitimately silence the microphone re-seeds the baseline and skips the verdict, which also
 * gives an automatic grace period: after unmuting, the next verdict needs fresh windows and can
 * never be computed across the muted stretch. See `evaluateMicSignal` for the rest of the
 * contract.
 */
export function startMicSignalMonitor({
  session,
  isInvite,
  dispatch,
  getState,
}: MicSignalMonitorArgs): MicSignalMonitor {
  const direction = isInvite ? 'outgoing' : 'incoming';

  let prev: LocalAudioStats = ESTABLISH_BASELINE;
  let lastDeviceId: string | null | undefined;
  let consecutiveDead = 0;
  let warningShown = false;
  let everHadAudio = false;
  /** The user closed the warning: stay quiet for the rest of the call on THIS device. */
  let dismissed = false;
  let sentryReported = false;
  let stopped = false;

  let initialTimer: ReturnType<typeof setTimeout> | null = null;
  let interval: ReturnType<typeof setInterval> | null = null;

  const takeDown = () => {
    if (warningShown) {
      dismissMicAudioWarning();
      warningShown = false;
    }
  };

  /** Start a fresh window from `stats`, so no verdict spans a period we could not judge. */
  const reseed = (stats: LocalAudioStats) => {
    prev = stats;
    consecutiveDead = 0;
    takeDown();
  };

  const raise = (reasons: string[]) => {
    log.warn(
      `NUACOM-LOG: the microphone is producing no audio on an ${direction} call (${reasons.join(
        ','
      )}), warning the user.`
    );

    warningShown = true;
    showMicAudioWarning(
      () => dispatch(setVoiceSettingsOpen(true)),
      () => {
        // Respected for the rest of the call. A device change re-arms it below.
        dismissed = true;
        warningShown = false;
      }
    );
  };

  const sample = async (isFirst: boolean) => {
    if (stopped) return;

    const call = getState().callCurrent.data.find(e => e.id === session.id);

    if (!call || !call.answered || call.finished) return;

    const pc: RTCPeerConnection =
      // @ts-ignore sip.js types the handler as the base interface.
      session.sessionDescriptionHandler?.peerConnection;

    if (!pc || pc.signalingState === 'closed') return;

    const next = await getLocalAudioStats(pc);

    if (stopped || !next) return;

    // A capture track reports a deviceId in getSettings(); a track sourced from a Web Audio
    // destination node does not. That is how the conference mix and announcement playback - both
    // of which replaceTrack() onto the senders - are recognised and left alone.
    const isMicrophoneTrack = !!next.deviceId;
    const isMixed =
      conferenceMixer.isActive() || call.status === actionTypes.CALL_CONFERENCE;

    if (
      call.mute ||
      call.hold ||
      isMixed ||
      !isMicrophoneTrack ||
      next.trackEnabled === false
    ) {
      reseed(next);
      return;
    }

    // A mid-call switch (`applyMicrophoneToActiveCalls`) replaces the sender track and restarts
    // its counters, and a device the user just chose deserves to be judged on its own evidence.
    if (lastDeviceId !== undefined && next.deviceId !== lastDeviceId) {
      lastDeviceId = next.deviceId;
      dismissed = false;
      everHadAudio = false;
      reseed(next);
      return;
    }

    lastDeviceId = next.deviceId;

    // Telemetry, on the first sample we were actually able to judge. Gating it this way is also
    // a fix: today a user who answers and immediately mutes raises a bogus Sentry warning.
    if (!sentryReported) {
      sentryReported = true;
      reportMicrophoneHealth(next, { direction, sessionId: session.id });
    }

    const verdict = evaluateMicSignal(prev, next);
    // Read before this window updates the latch, so a window is never judged on its own audio.
    const heardBefore = everHadAudio;

    prev = next;

    if (verdict.hadAudio) everHadAudio = true;

    if (!verdict.usable) {
      consecutiveDead = 0;
      return;
    }

    if (!verdict.dead) {
      consecutiveDead = 0;
      // The microphone recovered - or the user finally spoke. Either way, stop nagging.
      takeDown();
      return;
    }

    consecutiveDead += 1;

    // A microphone that has already been heard on this call gets more benefit of the doubt, but
    // only against silence: a fault on the track itself is unambiguous however well it worked a
    // moment ago, so it keeps the short threshold.
    const required =
      heardBefore && !verdict.deviceFault
        ? MIC_DEAD_CONSECUTIVE_SAMPLES_AFTER_AUDIO
        : MIC_DEAD_CONSECUTIVE_SAMPLES;

    // The first check already rests on the full delay since establish, so it stands alone. Later
    // verdicts are built from consecutive short windows to carry comparable weight.
    const proven = isFirst || consecutiveDead >= required;

    if (proven && !warningShown && !dismissed) {
      raise(verdict.reasons);
    }
  };

  const tick = (isFirst: boolean) => {
    // `getStats()` rejects on a peer connection that is closing; the next tick recovers.
    sample(isFirst).catch(err =>
      log.error('Failed to sample the microphone signal.', err)
    );
  };

  initialTimer = setTimeout(() => {
    initialTimer = null;
    tick(true);
    interval = setInterval(() => tick(false), MIC_CHECK_INTERVAL_MS);
  }, MIC_CHECK_INITIAL_DELAY_MS);

  return {
    stop: () => {
      stopped = true;

      if (initialTimer) {
        clearTimeout(initialTimer);
        initialTimer = null;
      }

      if (interval) {
        clearInterval(interval);
        interval = null;
      }

      takeDown();
    },
  };
}

export default startMicSignalMonitor;
